import type { CreatePatientPayload, GetPatientsPayload, Patient, UpdatePatientPayload } from '../../types/index.js';
import { baseApi } from '../base-api.js';

type PatientsResponse = { patients: Patient[] };
type PatientResponse = { patient: Patient };
type PatientMutationResponse = { patient: Patient; message: string };

const patientListTags = (result: Patient[] | undefined) =>
  result
    ? [...result.map(({ id }) => ({ type: 'Patient' as const, id })), { type: 'Patient' as const, id: 'LIST' }]
    : [{ type: 'Patient' as const, id: 'LIST' }];

/**
 * Mirrors `apps/server/src/modules/patient` (`patientController`) exactly. Every mutation
 * invalidates the `LIST` tag (covering every `getPatients` cache entry regardless of
 * page/sortBy/sortDirection/search) so the list is always refetched from the server — which
 * sorts server-side (default `created_at desc`, see `getPatientsSchema`) — instead of relying on
 * each app's previous local-insert logic (web prepended, mobile appended new patients to the
 * in-memory array, so the two apps disagreed on order until the next full refetch).
 */
export const patientApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPatients: builder.query<Patient[], GetPatientsPayload>({
      query: (payload) => ({ path: ['patients'], method: 'get', query: payload }),
      transformResponse: (response: PatientsResponse) => response.patients,
      providesTags: (result) => patientListTags(result),
    }),

    getPatientById: builder.query<Patient, string>({
      query: (id) => ({ path: ['patients', { param: 'patient_id', value: id }], method: 'get' }),
      transformResponse: (response: PatientResponse) => response.patient,
      providesTags: (_result, _error, id) => [{ type: 'Patient', id }],
    }),

    createPatient: builder.mutation<Patient, CreatePatientPayload>({
      query: (body) => ({ path: ['patients'], method: 'post', body }),
      transformResponse: (response: PatientMutationResponse) => response.patient,
      invalidatesTags: [{ type: 'Patient', id: 'LIST' }],
    }),

    updatePatient: builder.mutation<Patient, { id: string; payload: UpdatePatientPayload }>({
      query: ({ id, payload }) => ({
        path: ['patients', { param: 'patient_id', value: id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: PatientMutationResponse) => response.patient,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Patient', id },
        { type: 'Patient', id: 'LIST' },
      ],
    }),

    deletePatient: builder.mutation<void, string>({
      query: (id) => ({ path: ['patients', { param: 'patient_id', value: id }], method: 'delete' }),
      transformResponse: (): void => undefined,
      // The anamnesis row is ON DELETE CASCADE on patients(id), so its cached entry
      // must not survive the patient.
      invalidatesTags: (_result, _error, id) => [
        { type: 'Patient', id },
        { type: 'Patient', id: 'LIST' },
        { type: 'Anamnesis', id },
      ],
    }),
  }),
});

export const {
  useGetPatientsQuery,
  useGetPatientByIdQuery,
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useDeletePatientMutation,
} = patientApi;
